import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { Search, User, Phone, Mail, Stethoscope, Loader2, Camera, RefreshCw, Lock, Unlock, X, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList } from "@/components/ui/command";
import { Toaster, toast } from "@/components/ui/sonner";
import Odontogram from "@/components/Odontogram";
import PageHeader from "@/components/PageHeader";
import PendingOpportunitiesPanel from "@/components/PendingOpportunitiesPanel";
import { useAuth } from "@/contexts/AuthContext";
import { useAppData, usePatientSearch } from "@/hooks/useAppData";
import { usePianoCuraPriceRealign } from "@/hooks/usePianoCuraPriceRealign";
import { useOpportunityEditPrefill } from "@/hooks/useOpportunityEditPrefill";
import { PatientSearchResults, DoctorSelect, AssistantSelect, InsuranceSelect, DatePickerField } from "@/components/FormFields";
import { PhotoUpload } from "@/components/PhotoUpload";
import OpportunityFormBanners from "@/components/OpportunityFormBanners";
import { getStagePermission } from "@/utils/stagePermissions";
import { getPianoLock, setPianoLock } from "@/lib/piano-lock";
import {
  INDIRETTI_ONLY_TREATMENT_CODES,
  INDIRETTI_REPARTI,
  REPARTI_CONFIG,
  PIANO_CURA_MODES,
  STAGES_WHITELIST_INDIRETTI_SPLIT,
} from "@/lib/opportunity-constants";
import {
  buildOpportunityPayload,
  computePianoCuraMonetary,
  uploadAndLinkPhotos,
} from "@/lib/opportunity-submit";

const API = `${window.location.origin}/api`;

function App({ mode = "clinico" }) {
  const modeConfig = PIANO_CURA_MODES[mode] || PIANO_CURA_MODES.clinico;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Edit-mode: when the URL carries `?opportunityId=...` we are editing
  // an existing opportunity. The form is pre-populated from the locally
  // tracked record and the submit handler PUTs instead of POSTing.
  const editingOpportunityId = searchParams.get("opportunityId") || "";
  // Consult-only mode: URL carries `?readonly=1`. We pre-fill the form
  // identically to edit mode but disable every input and hide the Save
  // button so the user can review the plan without touching it. Used for
  // opportunities deleted from GHL where editing would fail anyway.
  // NOTE: admin bypass — gli admin ignorano il flag `?readonly=1` perché
  // devono poter modificare qualunque opp in qualunque stage (richiesta
  // utente 04/06/2026). Per i non-admin il flag continua a valere.
  // ECCEZIONE: il flag `?consult=1` (es. da Chiama Paziente) forza la
  // sola consultazione PER TUTTI gli utenti, admin inclusi. Le regole
  // di sblocco verranno definite successivamente (richiesta utente
  // 06/06/2026).
  const { user: authUser } = useAuth();
  const isAdmin = (authUser?.role || "") === "admin";
  const isConsultOnly = searchParams.get("consult") === "1";
  const isReadonly = isConsultOnly || (!isAdmin && searchParams.get("readonly") === "1");

  // Permesso FOTO effettivo dalla matrice stage_permissions per la
  // pipeline+stage attuale dell'opp. Gli admin hanno sempre canPhoto=true.
  // Per gli user: lookup nella matrice; default = false per stage non
  // configurati (più sicuro del default visione perché la foto può
  // sovrascrivere dati clinici). Quando `canPhoto=true` la sezione foto
  // resta abilitata anche in `?consult=1` consentendo all'utente di
  // caricare solo foto pur essendo il resto del form in sola lettura.
  // Richiesta utente 06/06/2026.
  // Nota: i dati pipeline/stage dell'opp arrivano dal backend dopo il
  // fetch (editingPrefill.original_pipeline_id / original_stage_id).
  // Virtual Duplicate mode: l'utente sta editando l'anteprima di una
  // duplicata pending dalla pagina Archivio. La sorgente NON verrà
  // modificata; il Salva invocherà `duplicate-to-indiretti` con
  // overrides per creare una NUOVA opp Indiretti. Richiesta utente
  // 31/05/2026.
  // Virtual Duplicate RIMOSSO il 04/06/2026 (richiesta utente). Il flusso
  // duplicazione → Indiretti è stato sostituito dallo split inline:
  // dropdown per riga nel piano di cura + 1 POST /split-indiretti
  // backend. I 3 search params (virtualDuplicate, dupTarget, dupStagedId)
  // restano dichiarati come no-op per non rompere eventuali URL
  // bookmarkati prima del rilascio.
  const virtualDuplicate = false;
  const dupTarget = "";
  const dupStagedId = "";
  const [editingMeta, setEditingMeta] = useState(null); // { revision_count, last_updated_at, last_updated_by_email, created_at }
  const [editingLoading, setEditingLoading] = useState(false);
  // Stage-lock state: true se l'opportunità in editing non è più nel suo
  // primo stage editabile (es. Reparto Clinico "Da autorizzare"). In quel
  // caso l'UI si comporta come se ci fosse `?readonly=1` ma con un
  // banner dedicato che spiega il motivo (stage attuale).
  const [stageLockedInfo, setStageLockedInfo] = useState(null); // { stageId, stageName }
  // Admin override: quando attivo permette di modificare il piano anche se
  // l'opp è uscita dallo stage editabile. Solo per account admin. Resetta
  // ogni volta che si cambia opp.
  const [adminUnlock, setAdminUnlock] = useState(false);
  // Sola lettura "effettiva": URL param ?readonly=1 OPPURE stage successivo
  // al primo (Da autorizzare / Preventivi da caricare).
  // `adminUnlock`: per gli admin = scelta corrente nella sessione (con
  // persistenza su DB via POST /opportunities/{id}/admin-unlock). Per gli
  // USER = stato pre-impostato da un admin (viene dal GET dell'opp). Se
  // True sblocca la pagina per chiunque la apra.
  // In modalità Virtual Duplicate la pagina è SEMPRE editable
  // (anche se sorgente è in stage-lock o readonly) perché le modifiche
  // andranno a una NUOVA opp Indiretti — la sorgente resta intatta.
  // ADMIN BYPASS (richiesta utente 06/06/2026): gli admin ignorano sempre
  // lo stage-lock — possono modificare qualunque opp in qualunque stage
  // senza dover attivare il toggle "Abilita modifica su questa scheda".
  // ECCEZIONE: il flag `?consult=1` (es. da Chiama Paziente) forza la
  // sola consultazione PER TUTTI, admin inclusi.
  const adminBypass = isAdmin && !isConsultOnly;
  // NOTE: `effectiveReadonly`, `photoOnlyMode`, e il calcolo del flag
  // "Modifica prezzo" sono spostati DOPO la dichiarazione di `reparto`
  // più sotto (servono `REPARTI_CONFIG[reparto]?.pipelineId`).

  // Snapshot of the IDs we want the dropdowns to display, kept in sync with
  // doctors/assistants/insurances arrays once they finish loading. Without
  // this the dropdowns can stay empty if the lookup tables arrive AFTER the
  // edit-mode fetch has already set the bare `medico`/`assicurazione` state.
  const [editingPrefill, setEditingPrefill] = useState(null);

  // Promemoria override (richiesta utente 06/06/2026):
  //   - Titolo "Promemoria Reparto Clinico" / "Promemoria Ortodonzia"
  //   - X di chiusura nella Navbar (gestita via sessionStorage in PromemoriaPage)
  //   - Foto SEMPRE sbloccate (canPhoto override true), indipendentemente
  //     dalla matrice stage_permissions
  const promemoriaKind = searchParams.get("promemoria");
  const isFromPromemoria = promemoriaKind === "clinico" || promemoriaKind === "orto";
  const effectiveTitle = isFromPromemoria
    ? (promemoriaKind === "orto" ? "Promemoria Ortodonzia" : "Promemoria Reparto Clinico")
    : modeConfig.title;

  // Permesso FOTO per l'opp corrente (matrice stage_permissions).
  // Admin sempre true; user → true solo se il flag FOTO è on nello stage.
  // Per opp in modalità "create" (senza editingPrefill) `canPhoto = true`
  // perché lo stage non è ancora definito → durante la creazione tutto
  // è permesso e il salvataggio finale farà l'enforcement backend.
  // NOTE: dichiarato DOPO `editingPrefill` per evitare ReferenceError da
  // Temporal Dead Zone (richiesta utente 06/06/2026).
  const canPhoto = (() => {
    // Promemoria → foto sempre sbloccate (richiesta utente 06/06/2026)
    if (isFromPromemoria) return true;
    if (!editingOpportunityId) return true;
    if (!editingPrefill) return false; // ancora in caricamento → conservativo
    const perm = getStagePermission(authUser, editingPrefill.original_pipeline_id, editingPrefill.original_stage_id);
    return perm.photo;
  })();
  // URLs of photos already attached to the opportunity in GHL. The user can
  // remove any of them locally (UI only). On submit we add the NEW uploads via
  // the link-photos endpoint (auto-append on GHL). NOTE: GHL FILE_UPLOAD fields
  // don't support removal via API — if the user removes existing photos here
  // we warn them to remove the file directly from GHL.
  const [existingPhotoUrls, setExistingPhotoUrls] = useState([]);
  const [initialExistingPhotoUrls, setInitialExistingPhotoUrls] = useState([]);

  // Session-wide "lock" on one of the two Piano di Cura pages.
  // Locked state is restored across refreshes (localStorage) and cleared
  // on login (see AuthContext). The other page becomes inaccessible while
  // this one is locked — navigation attempts are redirected here.
  const [lockedMode, setLockedMode] = useState(() => getPianoLock());
  useEffect(() => {
    const onChange = (e) => setLockedMode(e.detail);
    const onStorage = (e) => {
      if (e.key === "bdd_piano_lock") setLockedMode(getPianoLock());
    };
    window.addEventListener("bdd-piano-lock-change", onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("bdd-piano-lock-change", onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
  // If locked on the OTHER page, redirect immediately.
  useEffect(() => {
    if (lockedMode && lockedMode !== mode) {
      navigate(`/piano-cura-${lockedMode}`, { replace: true });
    }
  }, [lockedMode, mode, navigate]);

  const isLockedHere = lockedMode === mode;
  const toggleLock = () => {
    if (isLockedHere) {
      setPianoLock(null);
      setLockedMode(null);
      toast.info("Pagina sbloccata");
    } else {
      setPianoLock(mode);
      setLockedMode(mode);
      toast.success(`Pagina bloccata su ${modeConfig.title.replace(/^Piano di Cura\s*—\s*/, "")}`);
    }
  };
  // Form data
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [nome, setNome] = useState("");
  const [cognome, setCognome] = useState("");
  const [telefono, setTelefono] = useState("");
  const [email, setEmail] = useState("");
  const [medico, setMedico] = useState("");
  const [assistente, setAssistente] = useState("");
  const [assicurazione, setAssicurazione] = useState("");
  const [reparto, setReparto] = useState(modeConfig.defaultReparto);
  const [stageId, setStageId] = useState("");

  // Modalità submit: "save" (default) o "ai" (post-salvataggio redirect a
  // /preventivo/nuovo/{id} per usare l'assistente AI). Impostato al click
  // dei rispettivi bottoni prima di scatenare il submit del form.
  const submitModeRef = useRef("save");
  // When the route switches between Clinico and Ortodonzia the component
  // is reused (props change but no remount), so the reparto from the
  // previous mode lingers and the Pipeline dropdown shows empty because
  // the value isn't part of the new mode's options. Reset it whenever
  // the mode changes.
  useEffect(() => {
    setReparto(modeConfig.defaultReparto);
    setStageId("");
  }, [mode, modeConfig.defaultReparto]);

  // Flag "Modifica prezzo" (richiesta utente 10/06/2026): permette di
  // sovrascrivere a mano il prezzo (€) delle prestazioni marcate
  // `price_editable=true` nel listino, su QUALSIASI stage delle pipeline
  // Assicurazioni (Reparto Clinico + Ortodonzia, incluse legacy).
  // Admin sempre, USER solo con flag `modifica_prezzo`. Quando attivo
  // sblocca il form (analogo a adminBypass) così l'utente può cliccare e
  // modificare l'input €. Il filtro per-prestazione (solo quelle marcate
  // come price_editable) è gestito in `TrattamentoRow.isEditablePrice`.
  const ASSICURAZIONI_PIPELINE_IDS = useMemo(() => new Set([
    "529iRmd3Wh7bjdgfHpP9", // Assicurazioni Reparto Clinico
    "Eu6Xz0GLvHfN5PejJ4SV", // Assicurazioni Ortodonzia
    "tmyDM7u0ygOPtn9rYMQT", // Legacy clinico
    "MyxgABpCKYCkMRBoQMDc", // Legacy ortodonzia
  ]), []);
  const INDIRETTI_PIPELINE_IDS = useMemo(() => new Set([
    "he2K2EvoAaa3SxaikphM", // Indiretti - Reparto Clinico
    "YNar5ZkxXDvFp31UMmHY", // Indiretti - Ortodonzia
  ]), []);
  const currentPipelineId = (REPARTI_CONFIG[reparto]?.pipelineId) || "";
  const priceOverrideEnabled =
    !isConsultOnly &&
    (isAdmin || !!authUser?.modifica_prezzo) &&
    ASSICURAZIONI_PIPELINE_IDS.has(currentPipelineId);
  // NB: `fasdacPriceOverrideEnabled`, `anyPriceOverride`,
  // `effectiveReadonly` e `photoOnlyMode` sono definiti più sotto, dopo
  // `useAppData()` (servono `insurances` per rilevare Fasdac).
  const [stages, setStages] = useState([]);
  const [stagesLoading, setStagesLoading] = useState(false);
  const [dataPianoCura, setDataPianoCura] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [pianoCura, setPianoCura] = useState([]);
  // Tracks whether the user has interacted with at least one input.
  // The progress bar is hidden until this flips to true so that the
  // bar doesn't show up just because of the default reparto.
  const [userInteracted, setUserInteracted] = useState(false);
  const markInteraction = useCallback(() => setUserInteracted(true), []);

  // Promemoria pipelines (Reparto Clinico + Ortodonzia) are reminder-only
  // and don't carry a "data inserimento piano di cura" — the field is
  // hidden in the UI and excluded from the progress bar for those reparti.
  const isPromemoria = reparto === "promemoria_appuntamento" || reparto === "promemoria_ortodonzia";

  // Gate the Odontogram + photos until ALL header fields are complete.
  // This forces a deterministic data-entry order and avoids producing
  // partial opportunities with no patient/insurance/etc. — the form was
  // already enforcing these on submit but users were able to start drawing
  // teeth before realising required fields were missing.
  const stagePicked = !REPARTI_CONFIG[reparto]?.needsStageSelection || !!stageId;
  const headerComplete =
    !!selectedPatient
    && !!medico
    && !!assistente
    && !!assicurazione
    && !!reparto
    && stagePicked
    && (isPromemoria || !!dataPianoCura);

  // Progress bar: 7 mandatory milestones (reparto excluded because
  // it is auto-set with a default value). Linear progress: each
  // completed field fills exactly 1/7 of the bar.
  const progressSegments = [
    !!selectedPatient,
    !!medico,
    !!assistente,
    isPromemoria ? true : !!dataPianoCura,
    !!assicurazione,
    pianoCura.some((g) => g.denti.length > 0),
    pianoCura.some((g) => g.trattamenti.some((t) => t.treatmentCode)),
  ];
  const progressRatio = progressSegments.length
    ? progressSegments.filter(Boolean).length / progressSegments.length
    : 0;

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Custom hooks
  const { doctors, assistants, insurances, treatments, categories, baseCosts, treatmentsMeta, isLoadingData, isRefreshing, loadTreatments, refreshAll } = useAppData();

  // "Modifica prezzo Fasdac" (richiesta utente 02/10/2026): quando
  // l'assicurazione selezionata è Fasdac, TUTTE le prestazioni diventano
  // modificabili a mano (non solo le `price_editable`), sia per i
  // pazienti DIRETTI (pipeline Assicurazioni) sia INDIRETTI (pipeline
  // Indiretti). Stessi permessi del flag "Modifica prezzo": admin sempre,
  // USER solo con flag `modifica_prezzo`. Il backend ricalcola già
  // `note_1` e `monetaryValue` dal prezzo inviato. Definito qui (dopo
  // useAppData) perché serve `insurances` per il lookup del nome.
  const selectedInsuranceName = useMemo(() => {
    const ins = insurances.find((i) => i.id === assicurazione);
    return ins?.name || editingPrefill?.assicurazione_name || "";
  }, [insurances, assicurazione, editingPrefill]);
  const isFasdacInsurance = useMemo(
    () => selectedInsuranceName.toLowerCase().includes("fasdac"),
    [selectedInsuranceName],
  );
  const fasdacPriceOverrideEnabled =
    !isConsultOnly &&
    (isAdmin || !!authUser?.modifica_prezzo) &&
    isFasdacInsurance &&
    (ASSICURAZIONI_PIPELINE_IDS.has(currentPipelineId) || INDIRETTI_PIPELINE_IDS.has(currentPipelineId));
  // Sblocco form (analogo ad adminBypass) quando è attivo un qualsiasi
  // override prezzo, così l'utente può raggiungere e modificare gli input €.
  const anyPriceOverride = priceOverrideEnabled || fasdacPriceOverrideEnabled;
  const effectiveReadonly = !virtualDuplicate && !adminBypass && !anyPriceOverride && (isReadonly || (!!stageLockedInfo && !adminUnlock));
  // Photo-only mode: lo stage è oltre il primo editabile (es. Caricati /
  // Preventivi caricati), MA il backend lascia comunque caricare foto.
  // Si DISATTIVA se la scheda è sbloccata (`adminUnlock=true`) o se siamo
  // in virtualDuplicate (dove serve l'editor completo) o se l'utente è admin.
  const photoOnlyMode = !isReadonly && !virtualDuplicate && !adminBypass && !anyPriceOverride && !!stageLockedInfo && !adminUnlock;

  const { patients, searchQuery, setSearchQuery, isSearching } = usePatientSearch();

  // Prestazioni effettivamente visibili nell'odontogramma: alcune sono
  // riservate ai reparti "Indiretti" (vedi `INDIRETTI_ONLY_TREATMENT_CODES`)
  // e vanno nascoste sulle altre pipeline.
  const visibleTreatments = useMemo(() => {
    if (INDIRETTI_REPARTI.has(reparto)) return treatments;
    return treatments.filter(
      (t) => !INDIRETTI_ONLY_TREATMENT_CODES.has((t.code || "").toUpperCase())
    );
  }, [treatments, reparto]);

  // BUG FIX 04/06/2026 — Bug "salta l'assegnazione dell'assistente".
  // Se l'assistente salvato sull'opp è un contatto GHL che ha perso il
  // tag "assistente di studio" (ex-dipendente, tag editato a mano, ecc.),
  // non compare più in `/api/ghl/assistants`. Il `<Select>` shadcn allora
  // mostra il placeholder anche se lo state `assistente` contiene l'ID
  // corretto → l'utente pensa che l'assegnazione sia saltata e o ne
  // sceglie un altro o salva senza toccare (in quest'ultimo caso l'ID
  // viene preservato, ma la confusione è reale).
  // Fix: se in modalità edit l'ID corrente non è nel listino GHL ma il
  // backend ci ha passato `editingPrefill.assistente_name`, iniettiamo
  // un'opzione "ghost" disabled etichettata "(non più nel listino)" così
  // il nome resta visibile e l'utente capisce che l'assegnazione è
  // intatta. Idem per medico e assicurazione.
  // BUG FIX 06/06/2026: il ghost veniva aggiunto anche quando l'array
  // `assistants/doctors/insurances` era ancora vuoto (durante il caricamento
  // iniziale), causando un flash di "Mario Rossi (non più nel listino)"
  // anche per assistenti validi finché la lista non si popolava. Ora il
  // ghost viene creato SOLO dopo che la lista è effettivamente popolata
  // e l'ID corrente non è presente — una condizione "missing" reale.
  const assistantsForSelect = useMemo(() => {
    if (!assistente || assistants.length === 0 || assistants.some((a) => a.id === assistente)) return assistants;
    const ghostName = editingPrefill?.assistente_name || "Assistente non più nel listino";
    return [...assistants, { id: assistente, name: `${ghostName} (non più nel listino)`, _ghost: true }];
  }, [assistants, assistente, editingPrefill]);
  const doctorsForSelect = useMemo(() => {
    if (!medico || doctors.length === 0 || doctors.some((d) => d.id === medico)) return doctors;
    const ghostName = editingPrefill?.medico_name || "Medico non più nel listino";
    return [...doctors, { id: medico, name: `${ghostName} (non più nel listino)`, _ghost: true }];
  }, [doctors, medico, editingPrefill]);
  const insurancesForSelect = useMemo(() => {
    if (!assicurazione || insurances.length === 0 || insurances.some((i) => i.id === assicurazione)) return insurances;
    const ghostName = editingPrefill?.assicurazione_name || "Assicurazione non più nel listino";
    return [...insurances, { id: assicurazione, name: `${ghostName} (non più nel listino)`, _ghost: true }];
  }, [insurances, assicurazione, editingPrefill]);

  const handleRefresh = useCallback(() => {
    const ins = insurances.find((i) => i.id === assicurazione);
    refreshAll(ins?.name || "", reparto);
  }, [insurances, assicurazione, refreshAll, reparto]);

  // Re-load treatments when insurance OR reparto changes. Per i reparti
  // "Indiretti" il backend aggiunge le prestazioni del listino BDD
  // (foglio Base) a quelle dell'assicurazione.
  // BUG FIX 17/06/2026 — DEEP REFRESH ON EDIT OPEN:
  // Quando si apre una scheda in edit mode (`editingOpportunityId` set)
  // forziamo SEMPRE `bustCache: true` così il backend ritorna prezzi
  // freschi del listino assicurazione (no cache HTTP/browser) — necessario
  // perché altrimenti, su Safari/iOS o su connessioni lente, può
  // ritornare la risposta cachata di un'opp aperta in precedenza con un
  // altro listino, e l'audit bug-signature girerebbe su dati stale.
  // Effetto: ogni apertura di scheda è "self-healing" come se l'utente
  // avesse cliccato il bottone refresh.
  useEffect(() => {
    const ins = insurances.find((i) => i.id === assicurazione);
    loadTreatments(ins?.name || "", reparto, { bustCache: !!editingOpportunityId });
  }, [assicurazione, insurances, loadTreatments, reparto, editingOpportunityId]);

  // Insurance/sheet mismatch detection (Bug guard introdotto 31/05/2026
  // dopo il caso "Baldi Maria Teresa": InSalute selezionata su GHL ma
  // listino caduto su Base → prezzi BDD salvati ovunque). Vero quando:
  // - l'utente ha selezionato un'assicurazione
  // - il nome NON è esplicitamente "Base"/"BDD" (privato dichiarato)
  // - il backend ha risolto il foglio "Base" (fallback silenzioso)
  // Il banner si attiva e il submit è bloccato finché non si cambia
  // assicurazione o si conferma "Base"/"BDD" esplicito.
  const insuranceMismatch = useMemo(() => {
    if (!treatmentsMeta?.fallbackUsed) return false;
    const ins = insurances.find((i) => i.id === assicurazione);
    const insName = (ins?.name || treatmentsMeta?.insuranceQueried || "").trim();
    if (!insName) return false;
    const norm = insName.toLowerCase().replace(/^\d+\s*[-–]\s*/, "").trim();
    if (norm === "base" || norm === "bdd") return false;
    return true;
  }, [treatmentsMeta, insurances, assicurazione]);

  // Auto-realign treatment prices when the active price list changes.
  // Logica complessa (race-condition guards, auto-heal "firma del bug",
  // edit-mode first-load guard) estratta in un hook custom il 25/02/2026
  // per ridurre App.js. Vedi `/app/frontend/src/hooks/usePianoCuraPriceRealign.js`
  // per la documentazione completa dei 5 guards.
  const { userTouchedListinoRef } = usePianoCuraPriceRealign({
    treatments,
    treatmentsMeta,
    stageLockedInfo,
    pianoCura,
    setPianoCura,
    editingOpportunityId,
    assicurazione,
    reparto,
    insurances,
    baseCosts,
  });

  // Edit-mode: fetcha l'opportunità tracciata e pre-popola il form.
  // Logica estratta in `useOpportunityEditPrefill` (vedi hook per
  // dettagli sul self-healing e stage-lock). Il fetch viene rieseguito
  // ogni volta che `editingOpportunityId` cambia.
  const { data: editPrefillData, loading: editPrefillLoading } =
    useOpportunityEditPrefill({
      editingOpportunityId,
      isReadonly,
      virtualDuplicate,
      dupTarget,
    });

  // Mirror del loading nel state locale per compatibilità con i banner
  // esistenti (editingLoading).
  useEffect(() => {
    setEditingLoading(editPrefillLoading);
  }, [editPrefillLoading]);

  // Sincronizza i form-state con i dati ricevuti dal fetch edit-mode.
  // Quando `editingOpportunityId` viene rimosso (uscita da edit-mode),
  // resetta i bit dipendenti dall'opp. Quando arriva nuovo data, popola.
  useEffect(() => {
    if (!editingOpportunityId) {
      setEditingMeta(null);
      setEditingPrefill(null);
      setExistingPhotoUrls([]);
      setInitialExistingPhotoUrls([]);
      setStageLockedInfo(null);
      setAdminUnlock(false);
      return;
    }
    if (!editPrefillData) return;
    const d = editPrefillData;
    setSelectedPatient(d.selectedPatient);
    setNome(d.nome);
    setCognome(d.cognome);
    setTelefono(d.telefono);
    setEmail(d.email);
    setMedico(d.medico);
    setAssistente(d.assistente);
    setAssicurazione(d.assicurazione);
    if (d.reparto) setReparto(d.reparto);
    if (d.stageId) setStageId(d.stageId);
    if (d.dataPianoCura) setDataPianoCura(d.dataPianoCura);
    setPianoCura(d.pianoCura);
    setExistingPhotoUrls(d.existingPhotoUrls);
    setInitialExistingPhotoUrls(d.existingPhotoUrls);
    setEditingMeta(d.editingMeta);
    setEditingPrefill(d.editingPrefill);
    setStageLockedInfo(d.stageLockedInfo);
    if (d.userUnlock) setAdminUnlock(true);
    setUserInteracted(true);
  }, [editPrefillData, editingOpportunityId]);

  // Reconcile edit-mode prefill with the lookup tables. The arrays
  // (doctors / assistants / insurances) load asynchronously: when they
  // arrive AFTER the edit fetch, we re-apply the saved IDs so the
  // dropdowns actually show the original selection. Only sets the
  // value when the corresponding ID is present in the loaded list.
  //
  // BUG FIX 04/06/2026: questo effect rieseguiva ad OGNI cambio di
  // `assistants/doctors/insurances` (es. dopo click su "Aggiorna" che
  // chiama `loadBaseData({bustCache:true})`, generando un nuovo array
  // reference). Risultato: se l'utente aveva cambiato l'assistente nel
  // dropdown, la sua scelta veniva sovrascritta col valore originale
  // del prefill. Bug segnalato per "salta l'assegnazione dell'assistente".
  // Fix: applica il prefill SOLO se lo state corrente è ancora vuoto o
  // uguale al prefilled value — non sovrascriviamo mai una scelta
  // esplicita dell'utente.
  useEffect(() => {
    if (!editingPrefill) return;
    if (editingPrefill.medico_id && doctors.some((d) => d.id === editingPrefill.medico_id)) {
      if (medico === "" || medico === editingPrefill.medico_id) {
        setMedico(editingPrefill.medico_id);
      }
    }
    if (editingPrefill.assistente_id && assistants.some((a) => a.id === editingPrefill.assistente_id)) {
      if (assistente === "" || assistente === editingPrefill.assistente_id) {
        setAssistente(editingPrefill.assistente_id);
      }
    }
    if (editingPrefill.assicurazione_id && insurances.some((i) => i.id === editingPrefill.assicurazione_id)) {
      if (assicurazione === "" || assicurazione === editingPrefill.assicurazione_id) {
        setAssicurazione(editingPrefill.assicurazione_id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingPrefill, doctors, assistants, insurances]);

  // Pre-fill from "Apri Piano di Cura" shortcut on the Chiama-Paziente page.
  // The URL carries `contactId`, `patientName`, `phone`, `email`,
  // `doctorName` (and `doctorUserId`) so we can populate paziente + medico
  // without making the user re-search them. This only runs in CREATE mode
  // (no `opportunityId`) so it never clashes with the edit-mode prefill.
  // `doctorUserId` is matched against `doctors[].userId` first (unambiguous),
  // with a case-insensitive name match as a fallback.
  const prefillContactId = searchParams.get("contactId") || "";
  const prefillPatientName = searchParams.get("patientName") || "";
  const prefillPhone = searchParams.get("phone") || "";
  const prefillEmail = searchParams.get("email") || "";
  const prefillDoctorName = searchParams.get("doctorName") || "";
  const prefillDoctorUserId = searchParams.get("doctorUserId") || "";

  useEffect(() => {
    if (editingOpportunityId) return; // edit-mode owns the prefill
    if (!prefillContactId || !prefillPatientName) return;
    // Mirror the edit-mode name split so display stays consistent.
    const splitName = prefillPatientName.trim().split(/\s+/);
    const firstName = splitName.slice(0, -1).join(" ") || "";
    const lastName = splitName.slice(-1)[0] || "";
    setSelectedPatient({
      id: prefillContactId,
      firstName,
      lastName,
      phone: prefillPhone,
      email: prefillEmail,
    });
    setNome(firstName);
    setCognome(lastName);
    if (prefillPhone) setTelefono(prefillPhone);
    if (prefillEmail) setEmail(prefillEmail);
    setUserInteracted(true);
  }, [prefillContactId, prefillPatientName, prefillPhone, prefillEmail, editingOpportunityId]);

  useEffect(() => {
    if (editingOpportunityId) return;
    if (!prefillDoctorName && !prefillDoctorUserId) return;
    if (!doctors || doctors.length === 0) return;
    let match = null;
    if (prefillDoctorUserId) {
      match = doctors.find((d) => d.userId && d.userId === prefillDoctorUserId);
    }
    if (!match && prefillDoctorName) {
      const target = prefillDoctorName.trim().toLowerCase();
      match = doctors.find((d) => (d.name || "").trim().toLowerCase() === target)
        || doctors.find((d) => (d.name || "").toLowerCase().includes(target))
        || doctors.find((d) => target.includes((d.name || "").toLowerCase()));
    }
    if (match) setMedico(match.id);
  }, [doctors, prefillDoctorName, prefillDoctorUserId, editingOpportunityId]);

  // Live stage fetching for reparti that require stage selection
  // (e.g. "Promemoria Appuntamento"). Re-polls every 30s so that stages
  // added/removed directly in GHL show up here without manual refresh.
  useEffect(() => {
    const cfg = REPARTI_CONFIG[reparto];
    if (!cfg?.needsStageSelection) {
      setStages([]);
      return undefined;
    }
    let cancelled = false;

    const fetchStages = async () => {
      try {
        setStagesLoading(true);
        const { data } = await axios.get(
          `${API}/ghl/pipelines/${cfg.pipelineId}/stages`
        );
        if (cancelled) return;
        const list = Array.isArray(data?.stages) ? data.stages : [];
        setStages(list);
        // If the currently selected stage no longer exists in GHL, reset it.
        setStageId((prev) => (prev && list.some((s) => s.id === prev) ? prev : ""));
      } catch (err) {
        if (!cancelled) console.error("Fetch stages:", err);
      } finally {
        if (!cancelled) setStagesLoading(false);
      }
    };

    fetchStages();
    const iv = setInterval(fetchStages, 30000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [reparto]);

  // Patient selection handler
  const handlePatientSelect = useCallback((patient) => {
    markInteraction();
    setSelectedPatient(patient);
    setNome(patient.firstName || "");
    setCognome(patient.lastName || "");
    setTelefono(patient.phone || "");
    setEmail(patient.email || "");
    setSearchOpen(false);
    setSearchQuery("");
    toast.success(`Paziente ${patient.firstName} ${patient.lastName} selezionato`);
  }, [setSearchQuery, markInteraction]);

  // Photo-only upload: usato quando l'opp è in stage-lock (es. "Caricati"
  // o "Preventivi caricati"). Carica le nuove foto su GHL storage e le
  // linka all'opportunità SENZA chiamare il PUT del piano (che il backend
  // rifiuterebbe con 403). Skippa tutta la validazione dei campi del piano
  // perché il piano è immutabile in questa fase.
  const handlePhotoOnlyUpload = async (e) => {
    e.preventDefault();
    if (!editingOpportunityId) {
      toast.error("Apertura non valida — manca l'opportunityId");
      return;
    }
    if (photos.length === 0) {
      toast.error("Aggiungi almeno una foto prima di caricare");
      return;
    }
    setIsSubmitting(true);
    setUploadProgress(0);
    setUploadPhase(`Caricamento ${photos.length} nuove foto...`);
    try {
      let uploadsDone = 0;
      const uploadResults = await Promise.allSettled(
        photos.map((photo) => {
          const fd = new FormData();
          fd.append("files", photo.file, photo.name);
          return axios
            .post(`${API}/ghl/upload-to-storage`, fd, { timeout: 120000 })
            .then((res) => {
              uploadsDone++;
              setUploadProgress(Math.round((uploadsDone / photos.length) * 60));
              return res;
            });
        })
      );
      const uploadedUrls = [];
      const failedNames = [];
      uploadResults.forEach((result, i) => {
        const name = photos[i].name || photos[i].file.name;
        if (result.status === "fulfilled" && result.value.data?.url) {
          uploadedUrls.push(result.value.data.url);
        } else {
          failedNames.push(name);
        }
      });
      if (uploadedUrls.length > 0) {
        setUploadProgress(75);
        setUploadPhase(`Collegamento ${uploadedUrls.length} foto all'opportunità...`);
        const linkRes = await axios.post(
          `${API}/ghl/link-photos`,
          { opportunityId: editingOpportunityId, urls: uploadedUrls },
          { timeout: 120000 }
        );
        const linked = linkRes.data?.linked || 0;
        setUploadProgress(100);
        toast.success(`${linked} nuove foto aggiunte all'opportunità`);
        // Sposta le foto appena caricate nello stato "esistenti" e svuota la coda
        setExistingPhotoUrls((prev) => [...prev, ...uploadedUrls]);
        photos.forEach((p) => URL.revokeObjectURL(p.preview));
        setPhotos([]);
      }
      if (failedNames.length > 0) {
        toast.error(`Foto non caricate: ${failedNames.join(", ")}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Errore caricamento foto");
    } finally {
      setIsSubmitting(false);
      setTimeout(() => { setUploadProgress(0); setUploadPhase(""); }, 800);
    }
  };

  // Form submission - Two-step: create opportunity first, then upload photos
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPatient) { toast.error("Seleziona un paziente"); return; }
    if (!assicurazione) { toast.error("Seleziona un'assicurazione"); return; }
    if (!reparto) { toast.error("Seleziona una pipeline"); return; }
    if (REPARTI_CONFIG[reparto]?.needsStageSelection && !stageId) {
      toast.error("Seleziona uno stage della sequenza");
      return;
    }
    if (pianoCura.length === 0 || !pianoCura.some((g) => g.trattamenti.some((t) => t.treatmentCode))) { toast.error("Inserisci almeno un trattamento nel piano di cura"); return; }
    const groupsWithoutTreatment = pianoCura.filter((g) => g.denti.length > 0 && !g.trattamenti.some((t) => t.treatmentCode));
    if (groupsWithoutTreatment.length > 0) { toast.error("Hai selezionato dei denti senza associare una prestazione"); return; }

    setIsSubmitting(true);
    setUploadProgress(0);
    setUploadPhase("Creazione opportunità...");
    // splitResult: popolato solo nel flusso "split-Indiretti". Dichiarato
    // qui per essere accessibile sia nel branch update che nel toast finale.
    let splitResult = null;
    try {
      const opportunityData = buildOpportunityPayload(
        {
          selectedPatient, nome, cognome, telefono, email,
          medico, assistente, assicurazione, reparto,
          stageId, dataPianoCura, pianoCura, isPromemoria, editingOpportunityId,
        },
        { doctors, assistants, insurances },
        editingPrefill,
      );

      // --- Step 1: Create OR update opportunity ---
      let opportunityId;
      let totalCost;
      // Virtual Duplicate mode: l'utente è arrivato qui dal box
      // "Anteprima duplicazioni" cliccando il pennino su una entry
      // pending. La sorgente NON deve essere modificata e nemmeno una
      // NUOVA opp deve essere creata su GHL adesso. Le modifiche
      // restano LOCALI: salviamo `overrides` nello snapshot della
      // entry pendingDups e torniamo all'archivio. Il commit GHL
      // avviene SOLO quando l'utente preme "Salva" sul box "Anteprima
      // duplicazioni" (richiesta utente 31/05/2026).
      if (virtualDuplicate && editingOpportunityId && dupTarget) {
        setUploadPhase("Salvataggio modifiche locali...");
        setUploadProgress(40);
        const monetaryOverride = computePianoCuraMonetary(pianoCura);
        const overrides = {
          monetary_value: monetaryOverride,
          piano_cura: opportunityData.pianoCura,
          foto_urls: [...initialExistingPhotoUrls, ...existingPhotoUrls].filter((u, i, a) => a.indexOf(u) === i),
          // Salviamo anche dati visivi per la UI del box (preview totale
          // aggiornato senza dover ricalcolare lato Archivio).
          _display_total_cost: monetaryOverride,
          _display_n_trattamenti: opportunityData.pianoCura.reduce(
            (n, g) => n + (g.trattamenti || []).length, 0,
          ),
        };
        // Aggiorna lo snapshot pendingDups con l'override per questa entry.
        try {
          const raw = sessionStorage.getItem("pendingDupsSnapshot");
          if (raw) {
            const arr = JSON.parse(raw) || [];
            const updated = arr.map((p) =>
              p.id === dupStagedId
                ? { ...p, overrides, edited: true, edited_at: new Date().toISOString() }
                : p,
            );
            sessionStorage.setItem("pendingDupsSnapshot", JSON.stringify(updated));
          }
        } catch { /* ignore */ }
        setUploadProgress(90);
        toast.success("Modifiche salvate. Premi «Salva» nel box per confermare su GHL.");
        try { sessionStorage.removeItem("archiveReturnActive"); } catch {}
        navigate("/opportunita-create");
        setIsSubmitting(false);
        setUploadProgress(0);
        setUploadPhase("");
        return;
      }
      if (editingOpportunityId) {
        // Determina se l'utente ha marcato ALMENO una riga per Indiretti.
        // Se sì, invochiamo /split-indiretti che fa: PUT opp esistente +
        // POST 1 nuova opp Indiretti per ogni target (clinico/orto).
        // Foto: le NEW photos vengono caricate e linkate alla SOLA opp
        // originale (le foto vivono sul contact GHL, sono condivise).
        // Le opp Indiretti create ereditano `foto_urls` dell'opp main
        // tramite il payload `opportunityData.fotoUrls` (mirror locale).
        const hasIndirettoRows = (pianoCura || []).some(
          (g) => (g.trattamenti || []).some((t) => !!t.indirettoTarget),
        );
        // EDIT-MODE: do PUT for meta first, then upload+link any NEW photos
        // using the same auto-append flow as create-mode. Removals are NOT
        // applied to GHL (the FILE_UPLOAD field doesn't support deletion via
        // API) — we warn the user instead.
        // Admin override: se l'admin ha attivato lo sblocco, mandiamo il
        // flag al backend per bypassare lo stage-lock 403.
        let updateWasQueued = false;
        const updateData = {
          ...opportunityData,
          fotoUrls: existingPhotoUrls,
          admin_unlock: !!(isAdmin && adminUnlock),
        };
        if (hasIndirettoRows) {
          setUploadPhase("Aggiornamento opp + split Indiretti...");
          setUploadProgress(10);
          const splitResp = await axios.post(
            `${API}/ghl/opportunities/${editingOpportunityId}/split-indiretti`,
            updateData,
          );
          splitResult = splitResp.data;
          opportunityId = editingOpportunityId;
          totalCost = splitResult?.mainUpdate?.totalCost ?? 0;
        } else {
          setUploadPhase("Aggiornamento opportunità...");
          setUploadProgress(10);
          const response = await axios.put(
            `${API}/ghl/opportunities/${editingOpportunityId}`,
            updateData
          );
          opportunityId = editingOpportunityId;
          totalCost = response.data.totalCost;
          // Se la PUT è stata accodata (GHL down), il backend ritorna
          // queued:true → mostriamo un toast informativo invece del classico.
          if (response.data?.queued) {
            updateWasQueued = true;
          }
        }

        // Detect any existing photos the user removed from the UI: GHL doesn't
        // let us remove FILE_UPLOAD entries via API, so we surface a warning.
        const removedFromGHL = initialExistingPhotoUrls.filter(
          (u) => !existingPhotoUrls.includes(u)
        );
        if (removedFromGHL.length > 0) {
          toast.warning(
            `${removedFromGHL.length} foto rimosse solo da questa schermata`,
            { description: "GHL non supporta la rimozione foto via API. Eliminale direttamente in GHL se necessario." }
          );
        }

        // Upload NEW photos via helper (gestisce parallel upload + auto-append link).
        if (photos.length > 0) {
          setUploadProgress(20);
          await uploadAndLinkPhotos({
            photos,
            opportunityId,
            baseProgress: 20,
            uploadShare: 50,
            onProgress: setUploadProgress,
            onPhase: setUploadPhase,
            onToast: ({ type, message }) => toast[type]?.(message),
          });
        }

        setUploadProgress(95);
        if (updateWasQueued) {
          toast.success("Opportunità salvata — GHL lento, sincronizzazione in corso", {
            description: `Piano ${nome} ${cognome} - Totale: \u20ac${totalCost || 0}. La modifica verrà ritentata automaticamente entro 60s.`,
            duration: 6000,
          });
        } else {
          toast.success("Opportunità aggiornata!", {
            description: `Piano di cura per ${nome} ${cognome} - Totale: \u20ac${totalCost || 0}`,
          });
        }
      } else {
        // CREATE-MODE: original two-step flow
        const response = await axios.post(`${API}/ghl/opportunities`, opportunityData);

        // GHL sotto rate-limit: opp salvata in coda nel backend, verrà
        // sincronizzata automaticamente. Niente errore, niente perdita dati.
        if (response.data.pending) {
          toast.success("Opportunità salvata ✓", {
            description: photos.length > 0
              ? "GHL momentaneamente occupato: verrà sincronizzata automaticamente a breve. Ricordati di riaggiungere le foto dopo la sincronizzazione."
              : "GHL momentaneamente occupato: verrà sincronizzata automaticamente a breve.",
            duration: 8000,
          });
          await new Promise((r) => setTimeout(r, 400));
          setSelectedPatient(null);
          setNome(""); setCognome(""); setTelefono(""); setEmail("");
          setMedico(""); setAssistente(""); setAssicurazione(""); setReparto(modeConfig.defaultReparto); setStageId(""); setDataPianoCura(null);
          photos.forEach((p) => URL.revokeObjectURL(p.preview));
          setPhotos([]);
          setExistingPhotoUrls([]);
          setInitialExistingPhotoUrls([]);
          setPianoCura([]);
          setUserInteracted(false);
          return;
        }

        opportunityId = response.data.opportunityId;
        totalCost = response.data.totalCost;

        if (!opportunityId) {
          toast.error("Errore: opportunità creata ma ID non restituito");
          return;
        }

        setUploadProgress(photos.length > 0 ? 10 : 90);
        toast.success("Opportunità creata con successo!", {
          description: `Piano di cura per ${nome} ${cognome} - Totale: \u20ac${totalCost || 0}`,
        });

        // Step 2: Upload photos via helper (parallel upload + auto-append link)
        if (photos.length > 0) {
          setUploadProgress(15);
          await uploadAndLinkPhotos({
            photos,
            opportunityId,
            baseProgress: 15,
            uploadShare: 50,
            onProgress: setUploadProgress,
            onPhase: setUploadPhase,
            onToast: ({ type, message }) => toast[type]?.(message),
          });
        }
      }

      setUploadProgress(100);
      setUploadPhase("Completato!");

      // Toast riassuntivo per il flusso split-Indiretti (richiesta utente
      // 04/06/2026): comunica quante opp Indiretti sono state create.
      if (splitResult?.indirettiCreated?.length) {
        const items = splitResult.indirettiCreated.map((x) => {
          const label = x.target === "clinico" ? "Indiretti Reparto Clinico" : "Indiretti Ortodonzia";
          return `${label} (€${Math.round(x.totalCost || 0)})`;
        }).join(" · ");
        toast.success(`${splitResult.indirettiCreated.length} opp Indiretti create`, { description: items });
      }

      // Modalità "AI": dopo il salvataggio, invece del reset+redirect
      // normale, portiamo l'utente nella pagina di generazione preventivo.
      // Il piano di cura viene passato via sessionStorage per non doverlo
      // ricostruire dal backend (evita anche di aspettare la propagazione
      // GHL). Richiesta utente 11/07/2026.
      if (submitModeRef.current === "ai" && opportunityId) {
        const preventivoPayload = {
          opportunity_id: opportunityId,
          patient_name: `${nome} ${cognome}`.trim(),
          patient_id: selectedPatient?.id || "",
          insurance: assicurazione || "",
          reparto: reparto || "",
          piano_cura: pianoCura,
          total_euro: Number(totalCost || 0),
        };
        try {
          sessionStorage.setItem("preventivo_draft", JSON.stringify(preventivoPayload));
        } catch { /* ignore quota */ }
        submitModeRef.current = "save"; // reset per la prossima
        await new Promise((r) => setTimeout(r, 400));
        navigate(`/preventivo/nuovo/${opportunityId}`);
        return;
      }

      // Reset form after brief delay to show 100%
      await new Promise((r) => setTimeout(r, 600));
      setSelectedPatient(null);
      setNome(""); setCognome(""); setTelefono(""); setEmail("");
      setMedico(""); setAssistente(""); setAssicurazione(""); setReparto(modeConfig.defaultReparto); setStageId(""); setDataPianoCura(null);
      photos.forEach((p) => URL.revokeObjectURL(p.preview));
      setPhotos([]);
      setExistingPhotoUrls([]);
      setInitialExistingPhotoUrls([]);
      setPianoCura([]);
      setUserInteracted(false);
      // Drop the ?opportunityId=... param so the form is back in CREATE mode.
      if (editingOpportunityId) {
        setEditingMeta(null);
        setEditingPrefill(null);
        // Se l'utente è arrivato qui dall'Archivio Piani di Cura o dalla
        // Kanban Pipeline (flag settato in `handleEdit` di OpportunitaCreatePage
        // o in `OppDetailDialog` di PianiCuraPage), dopo il salvataggio lo
        // riportiamo lì con i suoi filtri/stato ripristinati automaticamente
        // dal sessionStorage. Altrimenti restiamo sulla stessa pagina in CREATE mode.
        let returnedToArchive = false;
        try {
          if (sessionStorage.getItem("pianiCuraReturnActive") === "1") {
            sessionStorage.removeItem("pianiCuraReturnActive");
            navigate("/piani-cura");
            returnedToArchive = true;
          } else if (sessionStorage.getItem("archiveReturnActive") === "1") {
            sessionStorage.removeItem("archiveReturnActive");
            navigate("/opportunita-create");
            returnedToArchive = true;
          }
        } catch {}
        if (!returnedToArchive) {
          navigate(`/piano-cura-${mode}`, { replace: true });
        }
      }
    } catch (error) {
      console.error("Error creating opportunity:", error);
      toast.error("Errore nella creazione dell'opportunità", { description: error.response?.data?.detail || "Riprova più tardi" });
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
      setUploadPhase("");
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f7fa] bg-grid-light flex items-start justify-center pb-4 sm:pb-8 overflow-x-hidden pt-[calc(7px+3.5rem+13px)]">
      <Toaster position="top-right" theme="light" />

      {/* Upload progress overlay */}
      {isSubmitting && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="upload-overlay">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-[90%] max-w-md space-y-5">
            <div className="flex items-center gap-3">
              <Loader2 className="w-6 h-6 text-[#3B42DD] animate-spin" />
              <h3 className="text-lg font-semibold text-slate-800">Invio in corso</h3>
            </div>
            <p className="text-sm text-slate-500">{uploadPhase || "Preparazione..."}</p>
            <div className="space-y-2">
              <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500 ease-out"
                  style={{
                    width: `${uploadProgress}%`,
                    background: uploadProgress === 100
                      ? "linear-gradient(90deg, #22c55e, #16a34a)"
                      : "linear-gradient(90deg, #3B42DD, #4285F4)",
                  }}
                  data-testid="upload-progress-bar"
                />
              </div>
              <p className="text-right text-sm font-medium tabular-nums" style={{ color: uploadProgress === 100 ? "#16a34a" : "#3B42DD" }}>
                {uploadProgress}%
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Progress bar (positioned right under the navbar) — only
          shown after the user has interacted with at least one field */}
      {userInteracted && progressSegments.some(Boolean) && (
        <div className="fixed top-[3.5rem] left-0 w-full h-[7px] bg-[#dce1eb] z-30">
          <div
            className="h-full bg-[#1a1fa8] transition-all duration-700 ease-in-out"
            style={{ width: `${progressRatio * 100}%` }}
          />
        </div>
      )}
      <div className="glow-orb glow-orb-1" />
      <div className="glow-orb glow-orb-2" />

      <div className="w-full px-4 lg:px-[100px] relative z-10">
        <PageHeader
          title={effectiveTitle}
          subtitle="Inserisci i dati per creare un nuovo piano di cura"
          actions={
            <>
              <button
                type="button"
                onClick={handleRefresh}
                disabled={isRefreshing || isLoadingData}
                data-testid="btn-refresh-data"
                title="Ricarica dati da GHL e Google Sheets"
                aria-label="Aggiorna dati"
                className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white transition-colors backdrop-blur-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`} />
              </button>
            </>
          }
        />

        <form onSubmit={handleSubmit} className="space-y-8 bg-white rounded-3xl pt-6 sm:pt-8 pb-6 sm:pb-8 px-3 sm:px-8 shadow-md -mt-8 relative z-10">
          <PendingOpportunitiesPanel />
          <OpportunityFormBanners
            isReadonly={isReadonly}
            stageLockedInfo={stageLockedInfo}
            isAdmin={isAdmin}
            adminUnlock={adminUnlock}
            setAdminUnlock={setAdminUnlock}
            editingOpportunityId={editingOpportunityId}
            effectiveReadonly={effectiveReadonly}
            editingMeta={editingMeta}
            editingLoading={editingLoading}
            navigate={navigate}
            mode={mode}
          />

          {/* 15/06/2026 — Quando l'opp è stage-locked MA lo stage rientra
              nella whitelist `allowIndirettoSplit` (es. "Da autorizzare",
              "Caricati", "Preventivi da caricare/caricati", "Caricato da
              Paziente"), il dropdown "→ Indiretti Clinico/Ortodonzia"
              PER RIGA deve restare cliccabile per consentire il
              riassegnamento. Un `<fieldset disabled>` però disabilita
              TUTTI i controlli figli a livello DOM (regola HTML, non
              sovrascrivibile via CSS). Soluzione: in quello scenario
              uso un `<fieldset>` non-disabled + classe `.pc-pseudo-readonly`
              che applica `pointer-events:none` a tutti i controlli interni
              tranne quelli con attributo `data-bypass-readonly` (oggi:
              solo i dropdown indiretto-split). Il resto del comportamento
              readonly resta identico. */}
          <fieldset
            disabled={effectiveReadonly && !stageLockedInfo?.allowIndirettoSplit}
            className={`space-y-8 border-0 p-0 m-0 min-w-0 ${
              effectiveReadonly ? "opacity-90" : ""
            } ${
              effectiveReadonly && stageLockedInfo?.allowIndirettoSplit ? "pc-pseudo-readonly" : ""
            }`}
          >

          {/* SEZIONE 1: Dati Paziente */}
          <div className="section-card rounded-2xl p-3 sm:p-8 space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <User className="w-5 h-5 text-[#4285F4]" />
              </div>
              <div>
                <h2 className="font-heading text-lg font-semibold text-[#333333]">Dati Paziente</h2>
                <p className="text-sm text-[#9b9b9b]">Informazioni del paziente e appuntamento</p>
              </div>
            </div>

            {/* Patient Search */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 opacity-0 animate-fade-in animation-delay-100">
              <div className="space-y-2">
                <Label className="label-custom flex items-center gap-2"><Search className="w-4 h-4" /> Ricerca Paziente</Label>
                <Popover open={searchOpen} onOpenChange={setSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" aria-expanded={searchOpen} data-testid="search-patient" className="w-full h-12 justify-start text-left font-normal custom-input hover:bg-slate-50">
                      {selectedPatient ? (
                        <span className="flex items-center gap-2 text-slate-800">
                          <User className="w-4 h-4 text-blue-600" /> {selectedPatient.lastName} {selectedPatient.firstName}
                        </span>
                      ) : (
                        <span className="text-slate-400">Cerca un paziente...</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[calc(200%+1.5rem)] p-0 popover-content-custom" align="start">
                    <Command className="command-custom" shouldFilter={false}>
                      <CommandInput placeholder="Cerca per nome, cognome o email..." value={searchQuery} onValueChange={setSearchQuery} className="h-12" />
                      <CommandList>
                        <PatientSearchResults isSearching={isSearching} patients={patients} searchQuery={searchQuery} selectedPatient={selectedPatient} onSelect={handlePatientSelect} />
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Contact details (readonly) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 opacity-0 animate-fade-in animation-delay-200">
              <div className="space-y-2">
                <Label htmlFor="telefono" className="label-custom flex items-center gap-2"><Phone className="w-4 h-4" /> Telefono</Label>
                <Input id="telefono" data-testid="input-telefono" value={telefono} readOnly disabled className="h-12 custom-input rounded-xl" placeholder="Numero di telefono" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="label-custom flex items-center gap-2"><Mail className="w-4 h-4" /> Email</Label>
                <Input id="email" data-testid="input-email" value={email} readOnly disabled className="h-12 custom-input rounded-xl" placeholder="Indirizzo email" />
              </div>
            </div>

            {/* BUG FIX 06/06/2026 (richiesta utente: "tutte le selezioni
                degli assistenti saltano all'apertura dell'opportunità"):
                il <Select> shadcn restava in stato `value=""` (placeholder)
                durante la finestra tra "lookup loaded" e "opp data
                loaded" → flash visivo di "Seleziona un assistente" prima
                di mostrare il valore corretto. Combiniamo entrambi gli
                stati di loading così il Select resta disabilitato con
                "Caricamento..." finché TUTTO è pronto. Lo stesso vale
                per Medico e Assicurazione. */}
            <DoctorSelect value={medico} onChange={(v) => { markInteraction(); setMedico(v); }} doctors={doctorsForSelect} isLoading={isLoadingData || editingLoading} />
            <AssistantSelect value={assistente} onChange={(v) => { markInteraction(); setAssistente(v); }} assistants={assistantsForSelect} isLoading={isLoadingData || editingLoading} />
            <InsuranceSelect value={assicurazione} onChange={(v) => { markInteraction(); userTouchedListinoRef.current = true; setAssicurazione(v); }} insurances={insurancesForSelect} isLoading={isLoadingData || editingLoading} />
            {/* Pipeline (ex Reparto) */}
            <div className="space-y-1.5" data-testid="reparto-select">
              <label className="text-xs font-medium text-[#555555] uppercase tracking-wider">Pipeline</label>
              <Select value={reparto} onValueChange={(v) => { markInteraction(); userTouchedListinoRef.current = true; setReparto(v); }}>
                <SelectTrigger className="h-12 custom-input rounded-xl" data-testid="select-reparto">
                  <SelectValue placeholder="Seleziona pipeline" />
                </SelectTrigger>
                <SelectContent>
                  {modeConfig.options.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Stage selector — shown only for reparti that need it.
                Dropdown style like the other selects: shows only the
                chosen stage when closed; opens a list with 5 stages
                visible at once and scroll for the rest. Stages are
                re-fetched from GHL every 30s for near real-time sync. */}
            {REPARTI_CONFIG[reparto]?.needsStageSelection && (
              <div className="space-y-1.5" data-testid="stage-selector">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-[#555555] uppercase tracking-wider">
                    Mese
                  </label>
                  {stagesLoading && (
                    <Loader2 className="w-3.5 h-3.5 text-slate-400 animate-spin" />
                  )}
                </div>
                <Select value={stageId} onValueChange={(v) => { markInteraction(); setStageId(v); }}>
                  <SelectTrigger
                    className="h-12 custom-input rounded-xl"
                    data-testid="select-stage"
                  >
                    <SelectValue placeholder="Scegli Mese" />
                  </SelectTrigger>
                  <SelectContent className="select-content" data-testid="stage-list">
                    {stages.length === 0 && !stagesLoading && (
                      <div className="px-3 py-3 text-sm text-slate-400">
                        Nessuno stage disponibile
                      </div>
                    )}
                    {stages.map((s) => (
                      <SelectItem
                        key={s.id}
                        value={s.id}
                        data-testid={`stage-item-${s.id}`}
                      >
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Date picker — shown after Reparto. Hidden when the reparto
                is one of the two "Promemoria" pipelines: those are pure
                appointment reminders so the "Data inserimento piano di cura"
                concept doesn't apply. */}
            {!isPromemoria && (
              <DatePickerField
                value={dataPianoCura}
                onChange={(v) => { markInteraction(); setDataPianoCura(v); }}
                open={calendarOpen}
                onOpenChange={setCalendarOpen}
                label="Data inserimento piano di cura"
              />
            )}
          </div>

          {/* SEZIONE 2: Piano di Cura */}
          <div className="section-card-gradient rounded-2xl p-3 sm:p-8 space-y-6 relative">
            <div className="flex items-center gap-3 pb-4 border-b border-slate-200/50">
              <div className="w-10 h-10 rounded-xl bg-[#4285F4] flex items-center justify-center">
                <Stethoscope className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-heading text-lg font-semibold text-[#333333]">Piano di Cura</h2>
                <p className="text-sm text-[#777777]">Prestazioni e documentazione</p>
              </div>
            </div>

            {/* Gate visivo sull'Odontogramma + Prestazioni: disabilitato
                finché l'header non è completo (Paziente, Medico,
                Assistente, Assicurazione, Reparto, Data inserimento).
                Stesso pattern usato dalla sezione foto sotto. Bypassato
                in modalità solo-foto (consult=1 + canPhoto) perché
                l'utente non può comunque modificare l'header. Richiesta
                utente 06/06/2026. */}
            <div className={(headerComplete || (isReadonly && canPhoto)) ? "space-y-4 opacity-0 animate-fade-in animation-delay-500" : "space-y-4 opacity-0 animate-fade-in animation-delay-500 pointer-events-none select-none opacity-50"}>
              <Odontogram
                pianoCura={pianoCura}
                setPianoCura={setPianoCura}
                treatments={visibleTreatments}
                categories={categories}
                // baseCosts ora SEMPRE valorizzato: serve per il dual-price
                // delle righe con `indirettoTarget` su pipeline non-Indiretti.
                baseCosts={baseCosts}
                // UI dual-price (radio Assic./BDD per ogni prestazione)
                // attiva per TUTTE le opp Indiretti (create, edit). Per le
                // pipeline assicurative la disabilitiamo globalmente, ma
                // ogni riga con `indirettoTarget` valorizzato la riattiva
                // localmente (vedi TreatmentGroupCard riga 1085).
                enableDualPriceUI={INDIRETTI_REPARTI.has(reparto)}
                // Dropdown "→ Indiretti Clinico / Ortodonzia" PER RIGA.
                // Visibile in edit-mode di un'opp esistente assicurativa
                // (non-Indiretti) ed editabile (stage iniziale O stage
                // in whitelist split-Indiretti). Al save chiama
                // `/split-indiretti` invece del PUT standard.
                enableIndirettoDropdown={
                  !!editingOpportunityId
                  && !INDIRETTI_REPARTI.has(reparto)
                  && (
                    !effectiveReadonly
                    || !!stageLockedInfo?.allowIndirettoSplit
                  )
                }
                // Flag "Modifica prezzo": permette di sovrascrivere a mano
                // il prezzo (€) delle prestazioni marcate `price_editable=true`
                // nel listino. Vale solo per le pipeline Assicurazioni
                // (Reparto Clinico + Ortodonzia, incluse legacy) e per
                // qualsiasi loro stage. Admin sempre, USER solo con flag
                // `modifica_prezzo`. Richiesta utente 10/06/2026.
                priceOverrideEnabled={priceOverrideEnabled}
                // "Modifica prezzo Fasdac" (02/10/2026): se l'assicurazione
                // è Fasdac, ogni prestazione del piano di cura diventa
                // editabile (diretti + indiretti). Vedi logica in App.js.
                fasdacPriceOverride={fasdacPriceOverrideEnabled}
              />
            </div>

            </div>
            </fieldset>

            {/* Sezione foto: abilitata se l'utente ha il permesso FOTO
                sullo stage corrente (matrice stage_permissions) OPPURE se
                non è in readonly. Permette agli user di caricare foto
                anche su opp aperte in `consult=1` quando il loro stage
                ha il flag FOTO attivo. Richiesta utente 06/06/2026. */}
            <fieldset disabled={isReadonly && !canPhoto} className="space-y-8 contents">
            <div className="section-card-gradient rounded-2xl p-3 sm:p-8 space-y-6 relative">

            {/* Gate "headerComplete" sul PhotoUpload: blocca il click su
                form incompleto in modalità create/edit normale. In modalità
                SOLO-FOTO (consult=1 + canPhoto) bypassiamo il gate perché
                l'utente non può comunque modificare l'header e i campi
                obbligatori sono già valorizzati lato server. Richiesta
                utente 06/06/2026. */}
            <div className={(headerComplete || (isReadonly && canPhoto)) ? "" : "pointer-events-none select-none opacity-50"}>
              <PhotoUpload photos={photos} setPhotos={setPhotos} />

            {editingOpportunityId && existingPhotoUrls.length > 0 && (
              <div className="space-y-2 opacity-0 animate-fade-in animation-delay-500" data-testid="existing-photos-section">
                <Label className="label-custom flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Foto già presenti su GHL ({existingPhotoUrls.length})
                  <span className="text-xs font-normal text-amber-600 ml-1">— GHL non supporta la rimozione via API; le X sono solo grafiche</span>
                </Label>
                <div className="flex flex-wrap gap-3">
                  {existingPhotoUrls.map((url, idx) => {
                    const isImg = /\.(jpe?g|png|gif|webp|bmp|tiff?|svg|heic|heif)(\?.*)?$/i.test(url);
                    return (
                      <div key={url} className="relative group">
                        {isImg ? (
                          <a href={url} target="_blank" rel="noopener noreferrer">
                            <img src={url} alt={`existing-${idx}`} className="w-20 h-20 object-cover rounded-lg border border-slate-200" />
                          </a>
                        ) : (
                          <a href={url} target="_blank" rel="noopener noreferrer" className="block w-20 h-20 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center">
                            <span className="text-xs font-bold text-slate-400 uppercase">FILE</span>
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => setExistingPhotoUrls((prev) => prev.filter((u) => u !== url))}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`remove-existing-photo-${idx}`}
                          title="Rimuovi foto da GHL"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            </div>
          </div>

          </fieldset>

          {/* Submit: visibile se l'utente può modificare (form completo)
              OPPURE se ha SOLO il permesso FOTO sullo stage corrente
              (può salvare giusto le foto). Nascosto se l'opp è in pura
              consultazione senza foto. Richiesta utente 06/06/2026. */}
          {(!effectiveReadonly || (isReadonly && canPhoto)) && (
          <div className="opacity-0 animate-fade-in animation-delay-500">
            {isReadonly && canPhoto && (
              <div className="mb-8 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                Modalità solo foto: il resto del form è in sola lettura. Salvando, verranno aggiornate solo le foto.
              </div>
            )}
            {insuranceMismatch && (
              <div
                className="mb-3 rounded-xl border-2 border-red-300 bg-red-50 p-4 text-sm text-red-900"
                data-testid="insurance-mismatch-banner"
              >
                <div className="font-bold mb-1">
                  ⚠️ Listino non valido per l'assicurazione selezionata
                </div>
                <div className="text-xs leading-relaxed">
                  Il backend non ha trovato un foglio listino per «{(insurances.find((i) => i.id === assicurazione)?.name) || treatmentsMeta?.insuranceQueried}»
                  e sta usando i prezzi del listino <strong>BDD (privato)</strong>.
                  I prezzi visualizzati NON sono quelli dell'assicurazione.
                  Verifica il nome dell'assicurazione su GHL o seleziona
                  esplicitamente «Base/BDD» se intendi usare il listino privato.
                  Il salvataggio è bloccato fino alla correzione.
                </div>
              </div>
            )}
            {(() => {
              const hasPiano = pianoCura.length > 0 && pianoCura.some((g) => g.trattamenti.some((t) => t.treatmentCode));
              const photoOnlySave = isReadonly && canPhoto;
              const canSubmit = photoOnlySave
                ? !!editingOpportunityId  // in modalità solo-foto basta avere un'opp esistente
                : selectedPatient && assicurazione && reparto && hasPiano && !insuranceMismatch;
              const canUseAi = (isAdmin || !!authUser?.ai_assistant) && !photoOnlySave && !virtualDuplicate;
              return (
                <div className="space-y-3">
                  <Button type="submit" onClick={() => { submitModeRef.current = "save"; }} disabled={isSubmitting || !canSubmit} data-testid="submit-opportunity" className={`w-full h-14 font-semibold rounded-xl transition-colors btn-glow ${canSubmit && !isSubmitting ? "bg-[#1a1fa8] hover:bg-[#f8dfd6] hover:text-[#1a1fa8] text-white shadow-md shadow-blue-500/15 hover:shadow-lg" : "bg-[#a8c8f7] text-white/80 cursor-not-allowed"}`}>
                    {isSubmitting ? (
                      <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Invio in corso...</span>
                    ) : photoOnlySave ? (
                      "Aggiorna foto"
                    ) : virtualDuplicate ? (
                      `Salva e Crea Duplicata in Indiretti ${dupTarget === "orto" ? "Ortodonzia" : "Reparto Clinico"}`
                    ) : editingOpportunityId ? (
                      "Aggiorna il piano di cura"
                    ) : (
                      "Invia il piano di cura"
                    )}
                  </Button>
                  {canUseAi && (
                    <Button
                      type="submit"
                      onClick={() => { submitModeRef.current = "ai"; }}
                      disabled={isSubmitting || !canSubmit}
                      data-testid="submit-opportunity-with-ai"
                      className={`w-full h-12 font-semibold rounded-xl transition-colors border ${canSubmit && !isSubmitting ? "bg-white text-violet-700 border-violet-200 hover:bg-violet-50 hover:border-violet-300" : "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"}`}
                    >
                      <span className="flex items-center justify-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        {editingOpportunityId ? "Aggiorna e Crea Preventivo (AI)" : "Crea Preventivo e invia Piano di Cura"}
                      </span>
                    </Button>
                  )}
                </div>
              );
            })()}
          </div>
          )}
          {/* Bottone Photo-only: visibile quando l'opp è in stage-lock e
              l'utente ha aggiunto nuove foto. Salta il PUT al piano e fa
              solo upload+link delle foto. */}
          {photoOnlyMode && (
            <div className="pt-4 opacity-0 animate-fade-in animation-delay-500">
              <Button
                type="button"
                onClick={handlePhotoOnlyUpload}
                disabled={isSubmitting || photos.length === 0}
                data-testid="submit-photo-only"
                className={`w-full h-14 font-semibold rounded-xl transition-colors btn-glow ${
                  !isSubmitting && photos.length > 0
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/15 hover:shadow-lg"
                    : "bg-emerald-200 text-white/80 cursor-not-allowed"
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Caricamento in corso...</span>
                ) : photos.length === 0 ? (
                  "Aggiungi nuove foto per caricarle"
                ) : (
                  `Carica ${photos.length} nuova${photos.length > 1 ? "" : ""} foto${photos.length > 1 ? "" : ""}`
                )}
              </Button>
              <p className="text-xs text-slate-500 mt-2 text-center">
                Le foto verranno aggiunte all&apos;opportunità senza modificare il piano di cura.
              </p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default App;
